import React, { Component, Fragment } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { hot } from 'react-hot-loader';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import 'bootstrap/dist/css/bootstrap.css';
import '../../scss/app.scss';
import { CookiesProvider } from 'react-cookie';
import Router from './Router';
import store from './store';
import ScrollToTop from './ScrollToTop';
import logOutExpiredSession from '../../shared/functions/session';
// import Paywall from './Paywall';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY);

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loaded: false,
      path: window.location.pathname,
    };
  }

  componentDidMount() {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = '956556e3-58b5-44b5-a724-ba011a82a000';
    // eslint-disable-next-line func-names
    (function () {
      const d = document;
      const s = d.createElement('script');
      s.src = 'https://client.crisp.chat/l.js';
      s.async = 1; d.getElementsByTagName('head')[0].appendChild(s);
    }());
    // Check immediately instead of waiting for load event
    // The load event may have already fired or may take a while
    if (document.readyState === 'complete') {
      this.checkLoginStatus();
    } else {
      window.addEventListener('load', () => {
        this.checkLoginStatus();
      });
    }
  }

  checkLoginStatus() {
    const { path } = this.state;
    const userStorage = JSON.parse(localStorage.getItem('user'));

    // Retry configuration
    const maxRetries = 3;
    const timeout = 10000; // 10 seconds

    const attemptLogin = (retryCount = 0) => {
      axios.get('/user', { timeout })
        .then((response) => {
          if (typeof response.data === 'object') {
            // ALL GOOD
            localStorage.setItem('user', JSON.stringify(response.data));
            // Accepted, no plan, and the plan page is due (UserHelperLib::shouldShowPlanPage):
            // the page first, as /logged_in does after a login. A founder accepted while
            // already logged in never passes through /logged_in again; this is their way to it.
            // The page counts the display, so the next boot opens the app.
            if (response.data.planPageDue && !path.startsWith('/ext/')) {
              window.location.replace(`${process.env.REACT_APP_DOMAIN_URL}/paywall`);
              return;
            }
            this.setState({ loading: false });
            setTimeout(() => this.startApp(response.data), 500);
          } else if (path.startsWith('/summary')) {
            // NO USER - ONLY FOR SUMMARY REVIEW
            this.setState({ loading: false });
            setTimeout(() => this.startApp(userStorage), 500);
          } else {
            // INVALID RESPONSE
            sessionStorage.clear();
            localStorage.clear();
            window.location.href = `${process.env.REACT_APP_DOMAIN_URL}/logout`;
          }
        })
        .catch((error) => {
          const isNetworkError = !error.response || error.code === 'ECONNABORTED';

          if (path.startsWith('/summary')) {
            // Summary pages don't require auth
            this.setState({ loading: false });
            setTimeout(() => this.startApp(userStorage), 500);
            return;
          }

          // Retry logic for network errors and timeouts
          if (isNetworkError && retryCount < maxRetries) {
            const delay = Math.min(1000 * (2 ** retryCount), 5000); // Exponential backoff, max 5s
            console.warn(`Login check failed (attempt ${retryCount + 1}/${maxRetries}). Retrying in ${delay}ms...`);

            setTimeout(() => {
              attemptLogin(retryCount + 1);
            }, delay);
          } else {
            // Max retries reached or non-network error
            logOutExpiredSession();
          }
        });
    };

    attemptLogin();
  }

  startApp(user) {
    this.setState({ loading: false, loaded: true, user });
  }

  render() {
    const {
      loaded,
      loading,
      user,
    } = this.state;

    return (
      <Provider store={store}>
        <Elements stripe={stripePromise}>
          <BrowserRouter>
            <CookiesProvider>
              <ScrollToTop>
                <Fragment>
                  {!loaded
                    ? (
                      <div className={`load${loading ? '' : ' loaded'}`}>
                        <div className="load__icon-wrap">
                          <svg className="load__icon">
                            <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                          </svg>
                        </div>
                      </div>
                    )
                    : (
                      <Router loggedIn={!!user} />
                    )
                    }
                </Fragment>
              </ScrollToTop>
            </CookiesProvider>
          </BrowserRouter>
        </Elements>
      </Provider>
    );
  }
}

export default hot(module)(App);
